import { mapValues } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { QUERY_KEYS } from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { useInvalidateQuery } from '../../../hooks/useInvalidateQuery';
import useMutateData from '../../../hooks/useMutateData';
import usePermissions from '../../../hooks/usePermissions';
import CommissionForm from './CommissionForm';
import CommissionTabHeader from './CommissionTabHeader';
import { agentCommissionConfig, CommissionFormData } from './commissionFormConfig';
import { AgentCommissionResponse } from './commissionTypes';
import useCommissionQueries from './useCommissionQueries';

const AgentCommissionTab = () => {
  const { selectedCRMUserId, userData, commissionData } = useCommissionQueries<AgentCommissionResponse>();
  const invalidateData = useInvalidateQuery();
  const { hasPermission } = usePermissions();
  const canEditCommission = hasPermission('editPartnerCommission');

  const { createData, updateData } = useMutateData(
    API_ROUTES.CRM.AGENT_COMMISSION,
    [QUERY_KEYS.crmCommissions, selectedCRMUserId],
    'crm'
  );

  const initialCommissionData: CommissionFormData = useMemo(
    () => ({
      ...agentCommissionConfig.defaultValues,
      playerCommissionType: commissionData?.type ?? agentCommissionConfig.defaultValues.playerCommissionType,
      playerCommissionPercentage:
        commissionData?.percentage?.toString() ?? agentCommissionConfig.defaultValues.playerCommissionPercentage,
      picks: commissionData?.picks
        ? mapValues(commissionData.picks, (value) => (value / 100).toString())
        : agentCommissionConfig.defaultValues.picks,
    }),
    [commissionData]
  );

  const onSave = useCallback(
    (data: CommissionFormData) => {
      const preparedData = agentCommissionConfig.prepareSubmitData(data);
      const onSuccess = () => invalidateData([QUERY_KEYS.crmCommissions, selectedCRMUserId]);

      if (!selectedCRMUserId) return;

      if (commissionData) {
        updateData(selectedCRMUserId, preparedData, onSuccess, 'Commission updated successfully');
      } else {
        createData({ ...preparedData, agentId: selectedCRMUserId }, onSuccess, 'Commission created successfully');
      }
    },
    [commissionData, createData, invalidateData, selectedCRMUserId, updateData]
  );

  return (
    <>
      <CommissionTabHeader user={userData} />
      <CommissionForm
        config={agentCommissionConfig}
        initialData={initialCommissionData}
        handleSave={onSave}
        disabled={!canEditCommission}
      />
    </>
  );
};

export default AgentCommissionTab;
